import { api, converse, constants, _converse } from '@converse/headless';
import './message-actions.js';
import './message-body.js';
import 'shared/components/dropdown.js';
import 'shared/modals/message-versions.js';
import 'shared/modals/user-details.js';
import 'shared/registry.js';
import 'plugins/muc-views/modals/occupant.js';
import tplFileProgress from './templates/file-progress.js';
import tplInfoMessage from './templates/info-message.js';
import tplMepMessage from 'plugins/muc-views/templates/mep-message.js';
import tplMessage from './templates/message.js';
import tplMessageText from './templates/message-text.js';
import tplRetraction from './templates/retraction.js';
import tplSpinner from 'templates/spinner.js';
import { ObservableElement } from 'shared/components/observable.js';
import { __ } from 'i18n';
import { alertSendError } from 'shared/chat/send-errors.js';

const { Strophe } = converse.env;
const { SUCCESS } = constants;

/**
 * Whether following a link leaves Converse for a web page. Other schemes, such
 * as `mailto:` and the `xmpp:` URIs Converse leaves to the OS, go to a local
 * app, and `blob:` URLs are files Converse made itself. Only the page Converse
 * runs on counts as its own, not every page on its origin.
 * @param {URL} url
 */
function isExternalURL(url) {
    if (!['http:', 'https:'].includes(url.protocol)) return false;

    return url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search;
}

export default class Message extends ObservableElement {
    /**
     * @typedef {import('shared/components/types').ObservableProperty} ObservableProperty
     */

    constructor() {
        super();
        this.model_with_messages = null;
        this.model = null;
        this.observable = /** @type {ObservableProperty} */ ('once');
        this.addEventListener('click', (ev) => this.onLinkClick(ev));
        // A middle-click doesn't fire `click`, but still opens the link in a new tab.
        this.addEventListener('auxclick', (ev) => ev.button === 1 && this.onLinkClick(ev));
    }

    static get properties() {
        return {
            ...super.properties,
            model_with_messages: { type: Object },
            model: { type: Object },
        };
    }

    async initialize() {
        super.initialize();
        await this.model_with_messages.initialized;
        await this.model_with_messages.messages.fetched;

        const settings = api.settings.get();
        this.listenTo(settings, 'change:render_media', () => {
            // Reset individual show/hide state of media
            this.model.save('hide_url_previews', undefined);
            this.requestUpdate();
        });

        this.listenTo(this.model_with_messages, 'change:first_unread_id', () => this.requestUpdate());
        this.listenTo(this.model, 'change', () => this.requestUpdate());

        // Whether a message counts as unencrypted is a fact about the
        // conversation, not about the message, so it changes without the message
        // changing. Toggling encryption has to repaint the history that is
        // already on screen.
        if (this.model.chatbox) {
            this.listenTo(this.model.chatbox, 'change:omemo_active', () => this.requestUpdate());
        }

        this.listenTo(this.model, 'contact:change', () => this.requestUpdate());
        this.listenTo(this.model, 'occupant:add', () => this.requestUpdate());
        this.listenTo(this.model, 'occupant:change', () => this.requestUpdate());
        this.listenTo(this.model, 'vcard:add', () => this.requestUpdate());
        this.listenTo(this.model, 'vcard:change', () => this.requestUpdate());
        this.requestUpdate();
    }

    render() {
        if (!this.model) {
            return '';
        } else if (this.show_spinner) {
            return tplSpinner();
        } else if (this.model.get('file') && this.model.get('upload') !== SUCCESS) {
            return this.renderFileProgress();
        } else if (['mep'].includes(this.model.get('type'))) {
            return this.renderMEPMessage();
        } else if (['error', 'info'].includes(this.model.get('type'))) {
            return this.renderInfoMessage();
        } else {
            return this.renderChatMessage();
        }
    }

    /**
     * @param {import('lit').TemplateResult|string} [tpl_time] - A timestamp to show right after the text
     */
    renderRetraction(tpl_time = '') {
        return tplRetraction(this, tpl_time);
    }

    /**
     * @param {import('lit').TemplateResult|string} [tpl_time] - A timestamp to show right after the text
     */
    renderMessageText(tpl_time = '') {
        return tplMessageText(this, tpl_time);
    }

    renderMEPMessage() {
        return tplMepMessage(this);
    }

    renderInfoMessage() {
        return tplInfoMessage(this);
    }

    renderFileProgress() {
        if (!this.model.file) {
            // Can happen when file upload failed and page was reloaded
            return '';
        }
        return tplFileProgress(this);
    }

    renderChatMessage() {
        return tplMessage(this);
    }

    shouldShowAvatar() {
        return (
            api.settings.get('show_message_avatar') &&
            !this.model.isMeCommand() &&
            ['chat', 'groupchat', 'normal'].includes(this.model.get('type'))
        );
    }

    /** @param {MouseEvent} ev */
    onImgClick(ev) {
        ev.preventDefault();
        const img = /** @type {HTMLImageElement} */ (ev.target);
        api.modal.show('converse-image-modal', { src: img.src, filename: img.dataset.filename }, ev);
    }

    /**
     * Ask before following a link out of the chat, since the sender chose where
     * it leads. Clicks that Converse handles itself, such as an image opening in
     * a modal or an `xmpp:` URI opening a chat, have already been prevented by
     * the time they bubble up to here.
     * @param {MouseEvent} ev
     */
    async onLinkClick(ev) {
        // Only an explicit `false` turns it off, so that a setting which was
        // never registered doesn't silently disable the confirmation.
        if (ev.defaultPrevented || api.settings.get('confirm_external_links') === false) return;

        const link = /** @type {Element} */ (ev.target).closest('a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;

        let url;
        try {
            url = new URL(link.href);
        } catch {
            url = null; // Unparseable, so there's no telling where it leads.
        }
        if (url && !isExternalURL(url)) return;

        ev.preventDefault();
        const confirmed = await api.confirm(__('Open external link?'), [
            url ? __('This link will take you to %1$s', url.hostname) : __('This link will take you to:'),
            link.href,
        ]);
        // Opened the way the link's `target="_blank" rel="noopener"` would have.
        // Clicking it again instead would hand the click back to the link's own
        // handlers, and browsers ignore the modifier keys of a scripted click,
        // except that a shift-click's request for a new window can be honoured.
        if (confirmed) window.open(link.href, '_blank', ev.shiftKey ? 'noopener,popup' : 'noopener');
    }

    /**
     * @param {import("lit").PropertyValues} changed
     */
    firstUpdated(changed) {
        // Messages whose ephemeral auto-removal is deferred (e.g. an OMEMO
        // "couldn't be decrypted" notice) should only be considered "seen" once
        // the message is in view AND the tab is focused, so we're confident the
        // user actually saw it before it's removed.
        this.observableRequireFocus = !!this.model?.get('defer_ephemeral_timer');
        super.firstUpdated(changed);
    }

    /**
     * Called (once) when this message has been seen by the user. For ephemeral
     * messages whose deletion was deferred until then, this is where we start
     * the auto-destruct countdown.
     * @param {IntersectionObserverEntry} entry
     */
    onVisibilityChanged(entry) {
        super.onVisibilityChanged(entry);
        if (this.model?.get('defer_ephemeral_timer')) {
            this.model.startEphemeralTimer();
        }
    }

    onUnfurlAnimationEnd() {
        if (this.model.get('url_preview_transition') === 'fade-out') {
            this.model.save({
                'hide_url_previews': true,
                'url_preview_transition': 'fade-in',
            });
        }
    }

    async onRetrySendClicked() {
        try {
            await this.model_with_messages.resendFailedMessage(this.model);
        } catch (e) {
            alertSendError(/** @type {Error} */ (e));
        }
    }

    async onSendUnencryptedClicked() {
        const confirmed = await api.confirm(__('Send without encryption?'), [
            __(
                'This message will be sent unencrypted. The server, and anyone else who can read the conversation, can read it.',
            ),
        ]);
        if (!confirmed) return;
        try {
            const { handled } = await api.hook('resendFailedMessageUnencrypted', this.model_with_messages, {
                message: this.model,
                handled: false,
            });
            if (!handled) {
                api.alert('error', __('Error'), [__('This message could not be sent without encryption.')]);
            }
        } catch (e) {
            alertSendError(/** @type {Error} */ (e));
        }
    }

    onDiscardSendClicked() {
        this.model_with_messages.discardFailedMessage(this.model);
    }

    async onRetryClicked() {
        this.show_spinner = true;
        this.requestUpdate();
        await api.trigger(this.model.get('retry_event_id'), { 'synchronous': true });
        this.model.destroy();
        this.parentElement.removeChild(this);
    }

    hasMentions() {
        const is_groupchat = this.model.get('type') === 'groupchat';
        return (
            is_groupchat && this.model.get('sender') === 'them' && this.model_with_messages.isUserMentioned(this.model)
        );
    }

    getOccupantAffiliation() {
        return this.model.occupant?.get('affiliation');
    }

    getOccupantRole() {
        return this.model.occupant?.get('role');
    }

    getExtraMessageClasses() {
        const is_action = this.model.isMeCommand() || this.model.isRetracted();
        const extra_classes = [
            this.model.isFollowup() ? 'chat-msg--followup' : null,
            this.model.get('is_delayed') ? 'delayed' : null,
            is_action ? 'chat-msg--action' : null,
            this.model.isRetracted() ? 'chat-msg--retracted' : null,
            this.model.get('type'),
            this.shouldShowAvatar() ? 'chat-msg--with-avatar' : null,
        ].map((c) => c);

        if (this.model.get('type') === 'groupchat') {
            extra_classes.push(this.getOccupantRole() ?? '');
            extra_classes.push(this.getOccupantAffiliation() ?? '');
            if (this.model.get('sender') === 'them' && this.hasMentions()) {
                extra_classes.push('mentioned');
            }
        }
        // A message that arrived in the clear while this conversation's
        // encryption is switched on. That mismatch is the case worth marking:
        // the composer says the conversation is encrypted, and this one message
        // was not. `omemo_active` is enough on its own to test — it is forced
        // back to false whenever `omemo_supported` goes away — so a chat that
        // cannot be encrypted never marks anything.
        if (this.model.chatbox?.get('omemo_active') && !this.model.get('is_encrypted')) {
            extra_classes.push('chat-msg--unencrypted');
        }

        if (this.model.get('correcting')) extra_classes.push('correcting');
        return extra_classes.filter((c) => c).join(' ');
    }

    getRetractionText() {
        const { moderated, moderated_by, moderated_by_id } = this.model.attributes;
        // The moderator's JID is optional, so a moderation may only identify
        // the moderator by occupant-id, or not at all.
        const is_moderated = moderated === 'retracted' || moderated_by || moderated_by_id;
        if (['groupchat', 'mep'].includes(this.model.get('type')) && is_moderated) {
            if (!this.model.mod) {
                const { occupants } = this.model_with_messages;
                this.model.mod =
                    (moderated_by_id && occupants.findOccupant({ 'occupant_id': moderated_by_id })) ||
                    (moderated_by && occupants.findOccupant({ 'jid': moderated_by })) ||
                    (moderated_by && occupants.findOccupant({ 'nick': Strophe.getResourceFromJid(moderated_by) }));
            }
            if (this.model.mod && this.model.mod === this.model_with_messages.getOwnOccupant()) {
                return __('You removed a message');
            }
            const modname = this.model.mod ? this.model.mod.getDisplayName() : __('A moderator');
            return __('%1$s removed a message', modname);
        } else {
            return this.model.get('sender') === 'me'
                ? __('You removed a message')
                : __('%1$s removed a message', this.model.getDisplayName());
        }
    }

    /** @param {MouseEvent} ev */
    showUserModal(ev) {
        if (this.model.get('sender') === 'me') {
            api.modal.show('converse-profile-modal', { model: _converse.state.xmppstatus }, ev);
        } else if (this.model.get('type') === 'groupchat') {
            ev.preventDefault();
            api.modal.show('converse-muc-occupant-modal', { model: this.model.getOccupant(), message: this.model }, ev);
        } else {
            ev.preventDefault();
            api.modal.show('converse-user-details-modal', { model: this.model_with_messages }, ev);
        }
    }

    /** @param {MouseEvent} ev */
    showMessageVersionsModal(ev) {
        ev.preventDefault();
        api.modal.show('converse-message-versions-modal', { 'model': this.model }, ev);
    }

    /** @param {MouseEvent} [ev] */
    toggleSpoilerMessage(ev) {
        ev?.preventDefault();
        this.model.save({ 'is_spoiler_visible': !this.model.get('is_spoiler_visible') });
    }
}

api.elements.define('converse-chat-message', Message);
